import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import TRANSFER_BANK_NAMES from '../constants/transferBanks'; 

// (임시 CSS)
const styles = {
  container: { display: 'flex', gap: '40px', flexWrap: 'wrap' },
  formSection: { flex: 1, minWidth: '300px' },
  listSection: { flex: 2, minWidth: '400px' },
  formGroup: { marginBottom: '15px' },
  label: { display: 'block', marginBottom: '5px', fontWeight: 'bold' },
  input: { width: '100%', padding: '8px', boxSizing: 'border-box' },
  yearInput: { width: '200px', padding: '8px', fontSize: '1rem', marginBottom: '20px' },
  button: { padding: '10px 15px', cursor: 'pointer', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px' },
  editButton: { padding: '5px 10px', cursor: 'pointer', backgroundColor: '#ffc107', color: 'black', border: 'none', borderRadius: '4px', marginRight: '5px' },
  deleteButton: { padding: '5px 10px', cursor: 'pointer', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px' },
  cancelButton: { padding: '10px 15px', cursor: 'pointer', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', marginLeft: '10px' },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: '10px' },
  th: { border: '1px solid #ddd', padding: '8px', background: '#f4f4f4', textAlign: 'center' },
  td: { border: '1px solid #ddd', padding: '8px', textAlign: 'center' },
  message: { marginTop: '15px' }
};

// 폼 초기화용 객체
const initialFormState = {
  projectName: '',
  dailyWorkHours: 0,
  weeklyFullTimeHours: 0,
  monthlyFullTimeHours: 0,
  monthlyLeaveHours: 0,
  leavePayRate: 0,
  withdrawalBankName: '',
  withdrawalAccount: '',
  accountHolder: '',
};

const initialEscrowState = {
  bankName: '',
  bankAccount: '',
  accountHolder: '',
};

function BankNameSelect({ id, name, value, onChange }) {
  const current = value || '';
  const extra = current && !TRANSFER_BANK_NAMES.includes(current) ? [current] : [];
  return (
    <select id={id} name={name} style={styles.input} value={current} onChange={onChange} required>
      <option value="">은행 선택</option>
      {extra.map((bankName) => (
        <option key={bankName} value={bankName}>{bankName}</option>
      ))}
      {TRANSFER_BANK_NAMES.map((bankName) => (
        <option key={bankName} value={bankName}>{bankName}</option>
      ))}
    </select>
  );
}

const ProjectSettingsPage = () => {
  // 1. 상태(State) 정의
  // ⬇️ [수정] 'selectedYear'의 기본값으로 현재 연도를 설정합니다.
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [projects, setProjects] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState(initialFormState);
  const [editingProject, setEditingProject] = useState(null);
  const [escrow, setEscrow] = useState(initialEscrowState);
  const [escrowMessage, setEscrowMessage] = useState('');
  const [escrowError, setEscrowError] = useState('');
  const [escrowSaving, setEscrowSaving] = useState(false); 

  // ( ... fetchProjects ... - 변경 없음)
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/projects?year=${selectedYear}`);
      setProjects(res.data);
    } catch (err) {
      setError(err.response?.data?.message || '사업 목록을 불러오는 데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  // ( ... useEffect (fetchProjects) ... - 변경 없음)
  useEffect(() => {
    fetchProjects();
    setEditingProject(null); 
    setFormData(initialFormState); 
  }, [fetchProjects]);

  useEffect(() => {
    const fetchEscrow = async () => {
      try {
        const res = await api.get('/projects/escrow-account');
        setEscrow({
          bankName: res.data?.bankName || '',
          bankAccount: res.data?.bankAccount || '',
          accountHolder: res.data?.accountHolder || '',
        });
      } catch (err) {
        setEscrowError(err.response?.data?.message || '예수금 통장 설정을 불러오지 못했습니다.');
      }
    };
    fetchEscrow();
  }, []); 

  // ( ... handleFormChange ... - 변경 없음)
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };
  
  // ( ... handleSubmit ... - 변경 없음)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    const dataToSend = {
      ...formData,
      year: selectedYear,
    };
    try {
      if (editingProject) {
        await api.put(`/projects/${editingProject.id}`, dataToSend);
        setMessage(`[${selectedYear}년] '${formData.projectName}' 사업 수정 성공!`);
      } else {
        await api.post('/projects', dataToSend);
        setMessage(`[${selectedYear}년] '${formData.projectName}' 사업 등록 성공!`);
      }
      setFormData(initialFormState);
      setEditingProject(null);
      fetchProjects(); 
    } catch (err) {
      setError(err.response?.data?.message || '작업에 실패했습니다.');
    }
  };

  // ( ... handleYearChange ... - 변경 없음)
  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value, 10));
  };
  
  // ( ... handleEditClick ... - 변경 없음)
  const handleEditClick = (project) => {
    setEditingProject(project); 
    setFormData(project); 
    setMessage('');
    setError('');
    window.scrollTo(0, 0); 
  };
  
  // ( ... handleDeleteClick ... - 변경 없음)
  const handleDeleteClick = async (project) => {
    if (window.confirm(`[${project.projectName}] 사업을 정말 삭제하시겠습니까?\n(할당된 참여자가 있으면 삭제할 수 없습니다.)`)) {
      try {
        const res = await api.delete(`/projects/${project.id}`);
        setMessage(res.data.message || '삭제되었습니다.');
        setError('');
        fetchProjects(); 
      } catch (err) {
        setError(err.response?.data?.message || '삭제에 실패했습니다.');
        setMessage('');
      }
    }
  };

  // ( ... handleCancelEdit ... - 변경 없음)
  const handleEscrowChange = (e) => {
    const { name, value } = e.target;
    setEscrow((prev) => ({ ...prev, [name]: value }));
  };

  const handleEscrowSubmit = async (e) => {
    e.preventDefault();
    setEscrowError('');
    setEscrowMessage('');
    setEscrowSaving(true);
    try {
      await api.put('/projects/escrow-account', {
        bankName: escrow.bankName.trim(),
        bankAccount: String(escrow.bankAccount).trim(),
        accountHolder: escrow.accountHolder.trim(),
      });
      setEscrowMessage('4대보험 예수금 통장을 저장했습니다.');
    } catch (err) {
      setEscrowError(err.response?.data?.message || '예수금 통장 저장에 실패했습니다.');
    } finally {
      setEscrowSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingProject(null);
    setFormData(initialFormState);
    setError('');
    setMessage('');
  };

  return (
    <div>
      <h2>사업 등록</h2>

      <section
        style={{
          border: '1px solid #d0d7de',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
          background: '#f8fafc',
        }}
      >
        <h3 style={{ marginTop: 0 }}>공통 설정 · 4대보험 예수금 통장</h3>
        <p style={{ marginTop: 0, color: '#475569', fontSize: '0.92rem' }}>
          개인공제 자동이체 금액을 넣는 기관 통장입니다. 연도·사업과 관계없이 한 번만 등록합니다.
        </p>
        {escrowMessage && <p style={{ ...styles.message, color: 'green' }}>{escrowMessage}</p>}
        {escrowError && <p style={{ ...styles.message, color: 'red' }}>{escrowError}</p>}
        <form onSubmit={handleEscrowSubmit}>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ ...styles.formGroup, flex: '1 1 180px', marginBottom: 0 }}>
              <label style={styles.label} htmlFor="escrowBankName">은행명</label>
              <BankNameSelect
                id="escrowBankName"
                name="bankName"
                value={escrow.bankName}
                onChange={handleEscrowChange}
              />
            </div>
            <div style={{ ...styles.formGroup, flex: '1 1 220px', marginBottom: 0 }}>
              <label style={styles.label} htmlFor="escrowBankAccount">계좌번호</label>
              <input
                type="text"
                id="escrowBankAccount"
                name="bankAccount"
                style={styles.input}
                value={escrow.bankAccount}
                onChange={handleEscrowChange}
                required
                placeholder="앞자리 0이 있어도 그대로 입력"
              />
            </div>
            <div style={{ ...styles.formGroup, flex: '1 1 180px', marginBottom: 0 }}>
              <label style={styles.label} htmlFor="escrowAccountHolder">예금주</label>
              <input
                type="text"
                id="escrowAccountHolder"
                name="accountHolder"
                style={styles.input}
                value={escrow.accountHolder}
                onChange={handleEscrowChange}
                required
              />
            </div>
            <button type="submit" style={styles.button} disabled={escrowSaving}>
              {escrowSaving ? '저장 중...' : '공통 설정 저장'}
            </button>
          </div>
        </form>
      </section>

      {/* [수정] <select>를 <input type="number">로 변경 */}
      <label htmlFor="year-select" style={styles.label}>기준 연도</label>
      <input
        type="number"
        id="year-select"
        style={styles.yearInput}
        value={selectedYear}
        onChange={handleYearChange}
        placeholder="연도 입력 (예: 2024)"
        min="2020"
        step="1"
      />
      
      <hr />
      
      {message && <p style={{...styles.message, color: 'green'}}>{message}</p>}
      {error && <p style={{...styles.message, color: 'red'}}>{error}</p>}

      <div style={styles.container}>
        {/* 1. 신규 사업 등록 폼 ( ... 변경 없음 ... ) */}
        <section style={styles.formSection}>
          <h3>{editingProject ? `[${editingProject.projectName}] 사업 수정` : `${selectedYear}년 신규 사업명 등록`}</h3>
          <form onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="projectName">사업명</label>
              <input type="text" id="projectName" name="projectName" style={styles.input}
                     value={formData.projectName} onChange={handleFormChange} required 
                     placeholder="반드시 노인일자리 시스템에서 사용하는 사업명만 사용" />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="dailyWorkHours">일근무시간 (1일 근무시간)</label>
              <input type="number" id="dailyWorkHours" name="dailyWorkHours" style={styles.input}
                     value={formData.dailyWorkHours} onChange={handleFormChange} step="1" />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="weeklyFullTimeHours">주만근시간 (1주 만근 기준)</label>
              <input type="number" id="weeklyFullTimeHours" name="weeklyFullTimeHours" style={styles.input}
                     value={formData.weeklyFullTimeHours} onChange={handleFormChange} step="1" />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="monthlyFullTimeHours">월만근시간 (1달 만근 기준)</label>
              <input type="number" id="monthlyFullTimeHours" name="monthlyFullTimeHours" style={styles.input}
                     value={formData.monthlyFullTimeHours} onChange={handleFormChange} step="1" />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="monthlyLeaveHours">월연차발생시간 (1달 만근 시)</label>
              <input type="number" id="monthlyLeaveHours" name="monthlyLeaveHours" style={styles.input}
                     value={formData.monthlyLeaveHours} onChange={handleFormChange} step="1" />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="leavePayRate">연차수당(원) (1시간당)</label>
              <input type="number" id="leavePayRate" name="leavePayRate" style={styles.input}
                     value={formData.leavePayRate} onChange={handleFormChange} step="1" />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="withdrawalBankName">은행명</label>
              <BankNameSelect
                id="withdrawalBankName"
                name="withdrawalBankName"
                value={formData.withdrawalBankName}
                onChange={handleFormChange}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="withdrawalAccount">사용계좌 (출금계좌)</label>
              <input type="text" id="withdrawalAccount" name="withdrawalAccount" style={styles.input}
                     value={formData.withdrawalAccount || ''} onChange={handleFormChange} required
                     placeholder="급여 자동이체 출금계좌. 앞자리 0 유지" />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="accountHolder">예금주</label>
              <input type="text" id="accountHolder" name="accountHolder" style={styles.input}
                     value={formData.accountHolder || ''} onChange={handleFormChange} required
                     placeholder="출금계좌 예금주" />
            </div>
            <button type="submit" style={styles.button}>
              {editingProject ? '수정 완료' : '등록하기'}
            </button>
            {editingProject && (
              <button type="button" style={styles.cancelButton} onClick={handleCancelEdit}>
                수정 취소
              </button>
            )}
          </form>
        </section>

        {/* 2. 등록된 사업 목록 ( ... 변경 없음 ... ) */}
        <section style={styles.listSection}>
          <h3>{selectedYear}년 등록된 사업 목록</h3>
          {loading ? <p>로딩 중...</p> : (
            <div style={{overflowX: 'auto'}}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>사업명</th>
                    <th style={styles.th}>월만근(h)</th>
                    <th style={styles.th}>월연차(h)</th>
                    <th style={styles.th}>연차수당(원)</th>
                    <th style={styles.th}>일근무(h)</th>
                    <th style={styles.th}>은행명</th>
                    <th style={styles.th}>사용계좌</th>
                    <th style={styles.th}>예금주</th>
                    <th style={styles.th}>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.length > 0 ? projects.map(p => (
                    <tr key={p.id}>
                      <td style={{...styles.td, textAlign: 'left'}}>{p.projectName}</td>
                      <td style={styles.td}>{p.monthlyFullTimeHours}</td>
                      <td style={styles.td}>{p.monthlyLeaveHours}</td>
                      <td style={styles.td}>{p.leavePayRate.toLocaleString()}</td>
                      <td style={styles.td}>{p.dailyWorkHours}</td>
                      <td style={styles.td}>{p.withdrawalBankName || '-'}</td>
                      <td style={styles.td}>{p.withdrawalAccount || '-'}</td>
                      <td style={styles.td}>{p.accountHolder || '-'}</td>
                      <td style={styles.td}>
                        <button style={styles.editButton} onClick={() => handleEditClick(p)}>수정</button>
                        <button style={styles.deleteButton} onClick={() => handleDeleteClick(p)}>삭제</button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="9" style={styles.td}>등록된 사업이 없습니다.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ProjectSettingsPage;